import { useEffect, useState, type ReactNode } from 'react';
import { Provider } from 'react-redux';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
/*
 * PoseidonFrontend's global stylesheet, imported by its `index.tsx`. It carries the base
 * reset and the `--app-modal-z-index` variable that lifts MUI dialogs above everything.
 * The ported pages assume it, exactly as they did in the old app.
 *
 * `PoseidonFrontend/src/app/bootstrap.min.css` is deliberately NOT carried over: nothing in
 * that project imports it (verified by searching every `.tsx`/`.ts`/`.css` there), so the
 * legacy screens never had bootstrap's grid styles either. The `row` / `col-md-6` class
 * names the ported inventories use are inert in both projects, which is why the layout is
 * provided by `EditModal` + `GeneralInput` instead.
 */
import '../app-old/app.css';
import initStore from '../main/store/redux/initStore';
import { setUser } from '../main/store/stores/user/user.store';
import { setMenu } from '../main/store/stores/user/menu.store';
import { setPermission } from '../main/store/stores/user/permission.store';
import { SetEnums } from '../main/store/stores/allEnums/allEnums.store';
import { ApiConstants } from '../main/constants/ApiConstants';
import axiosInstance from '../main/initializers/axios';
import JwtManager from '../main/utils/jwtManager';
import type { LegacyRequestConfig } from '../main/initializers/platformApiAdapter';
import { AppThemeProvider as LegacyThemeProvider } from '../main/theme/AppThemeContext';
import { LoaderProvider } from '../main/utils/LoaderContext';
import { NotificationProvider } from '../pages-old/mainLanding/Landing/NotificationContext';
import { i18nextInit } from '../main/initializers/i18n';
import type IUser from '../main/interfaces/IUser';
import type IResource from '../main/interfaces/IResouce';
import type IUserMenu from '../main/interfaces/IUserMenu';
import type IPermission from '../main/interfaces/IPermission';
import { useAppSelector } from './store';
import { FailureScreen } from './ErrorBoundary';
import type { AuthorizationResource, Organization, Profile } from '../core/api/types';

/**
 * Runtime bridge between the Platform session and the ported PoseidonFrontend tree
 * (`src/main/**` + `src/pages-old/**`).
 *
 * The ported screens read identity, navigation and permissions from three redux slices that
 * PoseidonFrontend filled from `/api/system/login` and `/api/system/user/info`. Architecture
 * 5.0 has neither endpoint: a Platform session is a Platform token pair, and the navigation
 * arrives as the Platform authorization snapshot. This component is where the two worlds
 * meet, so ported components keep calling `useGetUser()`, `useGetMenu()`,
 * `useGetPermission()` and `usePerformAction()` unchanged.
 *
 * It also restores the four providers the ported shell expects above it:
 *
 *   redux Provider           the ported store (`initStore`) — the shell, the sidebar/topbar
 *                            and every `useSelector` need it.
 *   Legacy AppThemeProvider  `BasicCli` reads its own theme context, a different React
 *                            context from PlatformFE's MUI theme.
 *   NotificationProvider     what the ported `NotificationIcon` (rendered by TopSection and
 *                            LeftSection) subscribes to.
 *   LoaderProvider           `showLoader()` / `hideLoader()` are called by the shared axios
 *                            instance on every request, so without this every request logs
 *                            and the spinner never appears.
 *
 * The store is created once per page load, exactly as `initApp()` did. `i18next` is
 * initialised here too, because the ported screens call `useTranslation()` and the Platform
 * app's own entry does not initialise it.
 */
let legacyStore: ReturnType<typeof initStore> | null = null;
let enumsRequested = false;

const getLegacyStore = () => {
  legacyStore ??= initStore(null, localStorage.getItem('i18nextLng') ?? 'en');
  return legacyStore;
};

/**
 * Platform role code → the legacy code the ported screens branch on.
 *
 * `RolesDetailsGrid` shows its cross-company picker and its "Actions" button when
 * `user.role.code === 'superadmin'`, and `RoleActionsPage` renders only for that code. In
 * the monolith that code identified the operator who could see every company. The Platform
 * equivalent is a holder of the `PLATFORM_ADMIN` system role (seeded
 * in script 003 with `OrganizationId IS NULL`), so that role holder
 * is bridged as `superadmin` and everyone else keeps their own role code.
 *
 * This is a display/permission bridge only: the server remains the authority, and every
 * admin endpoint independently enforces its own authorization policy.
 */
function toLegacyRoleCode(roles: Array<{ code: string; isSystem: boolean }> | undefined): string {
  if (!roles?.length) return '';
  const system = roles.find((role) => role.isSystem && role.code === 'PLATFORM_ADMIN');
  return system ? 'superadmin' : (roles.find(role => role.code === 'ADMIN' && !role.isSystem) ?? roles[0]).code;
}

/**
 * Maps the Platform identity onto the legacy `IUser` model.
 *
 * The numeric ids in `IUser` (`id`, `roleId`, `companyId`) existed so the legacy server
 * could filter by company; PlatformAPI derives the tenant from the token instead, and
 * Platform identities are GUIDs with no numeric equivalent. They therefore map to `0`
 * rather than being faked, and the organization's display data is carried across so the
 * shell header and the timezone helpers keep working.
 *
 * One consequence is worth stating because it is visible: `companyId: 0` means the ported
 * notification provider does not open its SignalR connection (its guard is
 * `!companyId || !accessToken`). PlatformAPI has no notification hub, so that is the
 * behaviour we want, and the provider's own `NOTIFICATION_HUB_ENABLED` flag documents how to
 * restore push delivery. The unread badge still works — the bridge changed that guard to key
 * on the token alone.
 */
function toLegacyUser(
  profile: Profile,
  organization: Organization | null,
  roles: Array<{ code: string; name: string; isSystem: boolean }> | undefined,
): IUser {
  const roleCode = toLegacyRoleCode(roles);
  return {
    id: 0,
    roleId: 0,
    companyId: 0,
    email: profile.email,
    userName: profile.userName,
    firstName: profile.firstName,
    lastName: profile.lastName,
    avatar: '',
    isDelete: false,
    isActive: true,
    companyName: organization?.name,
    /*
     * Carried through under a Platform name because `IUser`'s numeric `companyId` has no
     * Platform equivalent: the ported screens that need the tenant identity read this.
     */
    organizationId: organization?.id,
    /** `COMMUNITY` for a self-registered user: the shell hides the application menus then. */
    organizationCode: organization?.code,
    companyTimeZone: organization?.timeZone,
    companyCurrencySymbol: organization?.currencyCode,
    /*
     * The legacy per-user "page redirect" preference chose between the TechPOS product
     * dashboards. The Platform has one workspace, so the value is inert; it is kept as the legacy
     * numeric default (2 = Dashboard) rather than a path, because the ported users form posts it
     * back as `Number(data.pageRedirect)` and a path would send NaN.
     */
    pageRedirect: 2,
    role: roleCode
      ? ({
          id: 0,
          code: roleCode,
          name: roles?.find((role) => role.code === (roleCode === 'superadmin' ? 'PLATFORM_ADMIN' : roleCode))?.name ?? roleCode,
          // SuperAdmin or the business ADMIN; other system roles (e.g. customer roles) are not administrators.
          isAdministrator: roleCode === 'superadmin' || roleCode === 'ADMIN',
        } as IUser['role'])
      : undefined,
  } as IUser;
}

/**
 * Translates the granted Platform catalog into the legacy menu and permission shapes.
 *
 * The mapping is close to 1:1 on purpose: the Platform catalog is the successor of the
 * TechPOS `Resource` table that `IResource` models, and the migration carried the same
 * `url` / `label` / `title` / `sortOrder` values across. The ported shell turns a menu `url`
 * into a link through `main/utils/routeAccess.ts`, and `app/App.tsx` mounts one route per
 * catalog URL, so `url` is bridged verbatim.
 *
 * Two deliberate differences from the legacy payload:
 *
 *   - `id` is numeric in `IResource` and a GUID in the Platform catalog. It is used only as
 *     a React key and as the open/closed group key, so a stable per-snapshot sequential id
 *     is assigned instead of faking a legacy identity.
 *   - Hidden resources are excluded from the menu (the legacy server did the same with
 *     `Resource.Hide`) but still appear as permissions, because they are API routes a page
 *     has to be allowed to call.
 */
function buildLegacyAccess(resources: AuthorizationResource[]) {
  const ids = new Map<string, number>();
  let nextId = 1;
  const idOf = (resource: AuthorizationResource) => {
    let id = ids.get(resource.id);
    if (id === undefined) {
      id = nextId++;
      ids.set(resource.id, id);
    }
    return id;
  };

  const grantedMenus = resources.filter(
    (resource) => resource.kind?.toLowerCase() === 'menu' && resource.canRead && !resource.isHidden,
  );
  const menuIds = new Set(grantedMenus.map((resource) => resource.id));

  /**
   * The numeric id of the parent group this resource belongs to, or `undefined` when it
   * stands on its own. A resource only gets a parent if that parent is itself part of this
   * menu, which is the same test the grouping below applies: the snapshot is per product, so
   * a child whose parent lives in another product is rendered as a root rather than dropped.
   */
  const parentIdOf = (resource: AuthorizationResource) => {
    if (!resource.parentId || !menuIds.has(resource.parentId)) return undefined;
    return idOf(resources.find((candidate) => candidate.id === resource.parentId)!);
  };

  const toResource = (resource: AuthorizationResource): IResource =>
    ({
      id: idOf(resource),
      url: resource.url ?? '',
      method: resource.method ?? '',
      title: resource.title ?? '',
      label: resource.label ?? '',
      image: resource.icon ?? '',
      parentId: parentIdOf(resource),
      isPublic: false,
      hide: resource.isHidden ?? false,
      order: resource.sortOrder ?? 0,
      code: resource.code,
      isDelete: false,
    }) as IResource;

  const bySortOrder = (a: AuthorizationResource, b: AuthorizationResource) =>
    (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || (a.label ?? '').localeCompare(b.label ?? '');

  const menu: IUserMenu[] = grantedMenus
    .filter((resource) => !resource.parentId || !menuIds.has(resource.parentId))
    .sort(bySortOrder)
    .map((root) => ({
      item: toResource(root),
      subItems: grantedMenus
        .filter((resource) => resource.parentId === root.id)
        .sort(bySortOrder)
        .map(toResource),
    }));

  const permissions: IPermission[] = resources
    .filter((resource) => resource.kind?.toLowerCase() === 'api' && resource.canRead && resource.url)
    .map((resource) => ({
      name: resource.code,
      url: resource.url as string,
      isRead: resource.canRead,
      isWrite: resource.canWrite,
    }));

  return { menu, permissions };
}

export function LegacyRuntime({ children }: { children: ReactNode }) {
  const profile = useAppSelector((state) => state.session.profile);
  const organization = useAppSelector((state) => state.session.organization);
  const snapshot = useAppSelector((state) => state.session.snapshot);
  const status = useAppSelector((state) => state.session.status);
  const store = getLegacyStore();
  const [ready, setReady] = useState(false);
  /*
   * `i18next` readiness is part of this component's contract, not an incidental side effect.
   *
   * The ported theme provider calls `i18n.on('languageChanged', ...)` during its first render,
   * so rendering a ported child against an uninitialised i18n throws
   * `TypeError: i18n.on is not a function` and takes the tree down. `src/main.tsx` awaits the
   * bootstrap before mounting, which is what fixes production; this flag is the same guarantee
   * for anyone who mounts `LegacyRuntime` directly (tests, a future nested entry), where an
   * effect that merely *starts* the bootstrap is not enough.
   *
   * A failed bootstrap is surfaced rather than swallowed: without i18n the ported screens cannot
   * render at all, so showing the reason beats a spinner that never resolves.
   */
  const [i18nReady, setI18nReady] = useState(false);
  const [i18nError, setI18nError] = useState<unknown>(null);

  useEffect(() => {
    let cancelled = false;
    void i18nextInit()
      .then(() => {
        if (!cancelled) setI18nReady(true);
      })
      .catch((error: unknown) => {
        if (!cancelled) setI18nError(error);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    /*
     * Only an AUTHENTICATED session is bridged into the ported store.
     *
     * The landing at `/` is public, so this component also runs for a visitor with no session. The
     * session slice always carries a `profile` object (its initial state is an object of empty
     * strings, not `null`), so the old `if (!profile) return` guard let anonymous visitors through
     * and published a blank user. That was wrong twice over:
     *
     *   * `useGetUser()` returned a truthy blank object, so the landing rendered its signed-in
     *     user menu — an avatar with no name — instead of the "Log in" button;
     *   * an empty menu and permission set were published, which the ported guard reads as "no
     *     access" rather than "not signed in".
     *
     * With the bridge gated on the session status, `user` stays `null` for a visitor and the
     * ported markup takes its own signed-out branch. The ported screens themselves are all behind
     * `RequireAuth`, so they always arrive here authenticated.
     */
    if (status !== 'authenticated' || !profile?.id) {
      // Still `ready`: the public landing renders without a ported user, menu or permissions.
      setReady(true);
      return;
    }

    store.dispatch(setUser(toLegacyUser(profile, organization, snapshot?.roles)));

    if (snapshot?.resources?.length) {
      const { menu, permissions } = buildLegacyAccess(snapshot.resources);
      store.dispatch(setMenu(menu));
      store.dispatch(setPermission(permissions));
    }

    /*
     * `initStore` loads the option dictionaries only when it is handed a user, and it is called
     * here with `null` (the Platform session owns identity), so the request is made explicitly.
     *
     * `utility/all-enums` is a real PlatformAPI route (see `Endpoints/UtilityEndpoints.cs`) and
     * the only enums it returns are the ones a ported screen actually reads — so a failure here
     * is a genuine surprise rather than an expected 404, which is why the fallback is an empty
     * dictionary plus `skipErrorToast` rather than a message on every page load.
     *
     * Skipped entirely without a token: `/` is public, and an anonymous visit has no business
     * asking for it. The dictionary only matters to a screen that renders option lists, and none
     * of those render signed out.
     */
    if (!enumsRequested && JwtManager.accessToken) {
      enumsRequested = true;
      axiosInstance
        .get(ApiConstants.enum.allEnums, { skipErrorToast: true } satisfies LegacyRequestConfig)
        .then((response) => store.dispatch(SetEnums(response.data)))
        .catch(() => store.dispatch(SetEnums({})));
    }

    /*
     * Rendered children are gated on this so the ported guard never sees an empty menu on
     * the first paint. `BasicCli` renders the sidebar from the menu slice, so mounting a
     * page before the slice is filled would render the chrome without navigation.
     */
    setReady(true);
  }, [status, profile, organization, snapshot, store]);

  /*
   * A failed i18n bootstrap is fatal for the ported tree, so it is reported instead of leaving
   * a spinner that never resolves. No provider is mounted here: this screen has to render when
   * the providers are exactly what cannot be mounted.
   */
  if (i18nError) {
    return <FailureScreen title="Platform could not start" error={i18nError} />;
  }

  /*
   * `LegacyThemeProvider` is only mounted once i18next is initialised.
   *
   * It has to be the provider and not merely its children: the provider's own effect calls
   * `i18n.on('languageChanged', ...)`, so it throws on mount against an uninitialised i18n even
   * with no children rendered. That is precisely the crash this component used to cause.
   */
  return (
    <Provider store={store}>
      {i18nReady ? (
        <LegacyThemeProvider>
          <NotificationProvider>
            <LoaderProvider>
              <ToastContainer
                position="bottom-center"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss={false}
                draggable
                pauseOnHover
              />
              {/* Children also wait for the session/menu slices; see the `ready` effect. */}
              {ready ? children : <PreparingWorkspace />}
            </LoaderProvider>
          </NotificationProvider>
        </LegacyThemeProvider>
      ) : (
        <PreparingWorkspace />
      )}
    </Provider>
  );
}

/**
 * The pre-mount screen.
 *
 * Self-contained rather than the legacy `.full-loader` / `.spinner` pair: those classes live in
 * each product's own `styles.css`, not in the copied PoseidonFrontend `app.css`, so depending on
 * them would render an empty page for the one frame before the menu slice is filled — the exact
 * symptom this whole change set is about.
 */
function PreparingWorkspace() {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        gap: 12,
        fontFamily: 'Arial, Helvetica, sans-serif',
        color: '#475467',
      }}
    >
      <span
        style={{
          width: 28,
          height: 28,
          border: '3px solid #d0d5dd',
          borderTopColor: '#2563eb',
          borderRadius: '50%',
          animation: 'bcSpin .8s linear infinite',
        }}
      />
      <span>Preparing the Platform workspace…</span>
      <style>{'@keyframes bcSpin { to { transform: rotate(360deg); } }'}</style>
    </div>
  );
}

export default LegacyRuntime;
